import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays,
  LayoutDashboard,
  ShoppingBag,
  Gift,
  Store,
  UtensilsCrossed,
  CalendarRange,
  Images,
  Mail,
  Star,
  List,
  Palette,
  MessageCircle,
  Sparkles,
  Settings,
  LogOut,
  Trash2,
  Plus,
  Pencil,
  X,
  Percent,
  HelpCircle,
  Paintbrush,
  LayoutTemplate,
  Bot,
  Save,
  Users,
  Search,
  Megaphone,
  Bell,
  Layers,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { fileToBase64 } from "@/lib/api";
import { Monogram } from "@/components/layout/Header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MenuManager from "./MenuManager";
import EventsManager from "./EventsManager";
import GalleryManager from "./GalleryManager";
import TestimonialsManager from "./TestimonialsManager";
import NavigationManager from "./NavigationManager";
import OrdersManager from "./OrdersManager";
import GiftCardsManager from "./GiftCardsManager";
import DesignManager from "./DesignManager";
import ProductsManager from "./ProductsManager";
import DashboardManager from "./DashboardManager";
import ServicesManager from "./ServicesManager";
import PromotionsManager from "./PromotionsManager";
import FaqManager from "./FaqManager";
import ThemesManager from "./ThemesManager";
import ElementStylesManager from "./ElementStylesManager";
import SectionsManager from "./SectionsManager";
import AgentsManager from "./AgentsManager";
import BackupsManager from "./BackupsManager";
import MediaManager from "./MediaManager";
import ReservationsManager from "./ReservationsManager";
import CrmManager from "./CrmManager";
import AnalyticsPanel from "./AnalyticsPanel";
import SeoManager from "./SeoManager";
import MarketingManager from "./MarketingManager";
import NotificationsManager from "./NotificationsManager";
import TeamManager from "./TeamManager";
import SecurityManager from "./SecurityManager";
import FreeCanvasEditor from "./FreeCanvasEditor";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "reservations", label: "Réservations", icon: CalendarDays },
  { id: "clients", label: "Clients", icon: Users },
  { id: "commandes", label: "Commandes", icon: ShoppingBag },
  { id: "boutique", label: "Boutique", icon: Store },
  { id: "promotions", label: "Promotions", icon: Percent },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "sections", label: "Sections", icon: LayoutTemplate },
  { id: "editeur", label: "Éditeur visuel", icon: Layers },
  { id: "themes", label: "Thèmes", icon: Palette },
  { id: "styles", label: "Styles", icon: Paintbrush },
  { id: "agents", label: "Agents IA", icon: Bot },
  { id: "marketing", label: "Marketing", icon: Megaphone },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "medias", label: "Médias", icon: Images },
  { id: "sauvegardes", label: "Sauvegardes", icon: Save },
  { id: "seo", label: "SEO", icon: Search },
  { id: "services", label: "Services", icon: Sparkles },
  { id: "cartes-cadeaux", label: "Cartes cadeaux", icon: Gift },
  { id: "carte", label: "Carte", icon: UtensilsCrossed },
  { id: "evenements", label: "Événements", icon: CalendarRange },
  { id: "galerie", label: "Galerie", icon: Images },
  { id: "avis", label: "Avis", icon: Star },
  { id: "navigation", label: "Navigation", icon: List },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "design", label: "Design", icon: Palette },
  { id: "equipe", label: "Équipe", icon: ShieldCheck },
  { id: "securite", label: "Sécurité", icon: ShieldCheck },
  { id: "reglages", label: "Réglages", icon: Settings },
];

function MessagesManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    toast.success("Message supprimé.");
    load();
  };

  if (items.length === 0)
    return <p className="text-sm text-ivory/40" data-testid="messages-empty">Aucun message pour le moment.</p>;

  return (
    <div className="space-y-4" data-testid="messages-list">
      {items.map((m) => (
        <div key={m.id} className="rounded-lg border border-white/10 bg-coal/60 p-5" data-testid={`message-${m.id}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-ivory">
                {m.name} {m.subject && <span className="text-ivory/40">— {m.subject}</span>}
              </p>
              <p className="text-xs text-ivory/45">{m.email}</p>
              {m.activity && (
                <span className="mt-1.5 inline-block rounded border border-gold/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold" data-testid={`message-activity-${m.id}`}>
                  {m.activity}
                </span>
              )}
            </div>
            <button
              onClick={() => remove(m.id)}
              aria-label="Supprimer"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400"
              data-testid={`message-delete-${m.id}`}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-ivory/60">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

const TEXT_FIELDS = [
  { key: "home_intro", label: "Accueil — texte d'introduction" },
  { key: "hero_subtitle", label: "Accueil — sous-titre du hero" },
  { key: "bar_intro", label: "Le Bar — texte d'introduction" },
  { key: "fidelite", label: "Fidélité — texte d'introduction" },
  { key: "comptoir_intro", label: "Comptoir — texte d'introduction" },
  { key: "services_intro", label: "Maasaï Services — texte d'introduction" },
  { key: "signature", label: "Signature / Logo (pied de page)" },
  { key: "universe_desc_palais", label: "Univers — description Palais Maasaï" },
  { key: "universe_desc_comptoir", label: "Univers — description Le Comptoir" },
  { key: "universe_desc_services", label: "Univers — description Maasaï Services" },
  { key: "story_01", label: "Histoire 01 — Nos origines" },
  { key: "story_02", label: "Histoire 02 — Notre philosophie" },
  { key: "story_03", label: "Histoire 03 — Notre cuisine" },
  { key: "story_04", label: "Histoire 04 — Notre savoir-faire" },
  { key: "story_05", label: "Histoire 05 — Nos valeurs" },
  { key: "story_06", label: "Histoire 06 — Un lieu de rencontres" },
  { key: "story_07", label: "Histoire 07 — Notre évolution" },
  { key: "story_08", label: "Histoire 08 — Notre avenir" },
  { key: "cgv", label: "CGV personnalisées (remplace le texte par défaut si rempli)" },
];

function SettingsManager() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setSettings((s) => ({ ...s, hero_image: base64 }));
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setSettings((s) => ({ ...s, logo: base64 }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        hero_image: settings.hero_image,
        logo: settings.logo || "",
        instagram_url: settings.instagram_url || "",
        facebook_url: settings.facebook_url || "",
        google_url: settings.google_url || "",
        contact: settings.contact || {},
        hours: settings.hours || {},
        deposit_per_person: parseFloat(settings.deposit_per_person) || 15,
        capacity_per_slot: parseInt(settings.capacity_per_slot, 10) || 40,
        google_api_key: settings.google_api_key || "",
        google_place_id: settings.google_place_id || "",
        google_rating: settings.google_rating || "",
        universe_photos: settings.universe_photos || {},
        page_heroes: settings.page_heroes || {},
        texts: settings.texts || {},
      });
      toast.success("Réglages enregistrés.");
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="max-w-2xl space-y-10" data-testid="settings-manager">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Image du Hero (accueil)
        </h3>
        <div className="mt-4 overflow-hidden rounded-lg border border-white/10">
          <img src={settings.hero_image} alt="Hero" className="h-48 w-full object-cover" data-testid="settings-hero-preview" />
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={uploadHero}
          className="mt-4 text-sm text-ivory/60 file:mr-4 file:rounded-md file:border-0 file:bg-copper file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
          data-testid="settings-hero-upload"
        />
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Logo officiel</h3>
        <p className="mt-2 text-xs text-ivory/40">
          Affiché dans l'en-tête, à gauche du nom Palais Maasaï.
        </p>
        {settings.logo && (
          <div className="mt-4 inline-block rounded-lg border border-white/10 bg-white/5 p-4">
            <img src={settings.logo} alt="Logo" className="h-14 w-auto" data-testid="settings-logo-preview" />
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={uploadLogo}
          className="mt-4 block text-sm text-ivory/60 file:mr-4 file:rounded-md file:border-0 file:bg-copper file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
          data-testid="settings-logo-upload"
        />
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Photos des activités (Univers Maasaï)
        </h3>
        <p className="mt-2 text-xs text-ivory/40">
          Affichées dans les sections « Découvrez l'Univers Maasaï » entre les activités.
        </p>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          {[
            { key: "palais", label: "Palais Maasaï" },
            { key: "comptoir", label: "Le Comptoir Maasaï" },
            { key: "services", label: "Maasaï Services" },
          ].map((space) => (
            <div key={space.key} className="rounded-lg border border-white/10 p-4" data-testid={`settings-universe-photo-${space.key}`}>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ivory/60">
                {space.label}
              </p>
              {(settings.universe_photos || {})[space.key] && (
                <img
                  src={settings.universe_photos[space.key]}
                  alt={space.label}
                  className="mb-3 h-20 w-full rounded-md object-cover"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const base64 = await fileToBase64(file);
                  setSettings((s) => ({
                    ...s,
                    universe_photos: { ...(s.universe_photos || {}), [space.key]: base64 },
                  }));
                }}
                className="w-full text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-copper file:px-3 file:py-1.5 file:text-[10px] file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
                data-testid={`settings-universe-upload-${space.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Bandeaux des pages (image, titre, description)
        </h3>
        <p className="mt-2 text-xs text-ivory/40">
          Affichés en haut des pages Maasaï Services et Comptoir Maasaï. Le bandeau de la page
          d'accueil se gère via « Image du Hero » ci-dessus.
        </p>
        <div className="mt-4 space-y-5">
          {[
            { key: "services", label: "Maasaï Services", fallback: "Ménage, nettoyage & entretien" },
            { key: "comptoir", label: "Le Comptoir Maasaï", fallback: "La boutique du Palais" },
          ].map((page) => {
            const hero = (settings.page_heroes || {})[page.key] || {};
            const update = (patch) =>
              setSettings((s) => ({
                ...s,
                page_heroes: {
                  ...(s.page_heroes || {}),
                  [page.key]: { ...((s.page_heroes || {})[page.key] || {}), ...patch },
                },
              }));
            return (
              <div key={page.key} className="rounded-lg border border-white/10 p-4" data-testid={`settings-hero-${page.key}`}>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ivory/60">
                  {page.label}
                </p>
                {hero.image && (
                  <img src={hero.image} alt={page.label} className="mb-3 h-24 w-full rounded-md object-cover" data-testid={`settings-hero-${page.key}-preview`} />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    update({ image: await fileToBase64(file) });
                  }}
                  className="w-full text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-copper file:px-3 file:py-1.5 file:text-[10px] file:font-semibold file:uppercase file:tracking-wider file:text-ivory"
                  data-testid={`settings-hero-${page.key}-upload`}
                />
                {hero.image && (
                  <button
                    type="button"
                    onClick={() => update({ image: "" })}
                    className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-ivory/40 transition-colors hover:text-red-400"
                    data-testid={`settings-hero-${page.key}-remove`}
                  >
                    Supprimer l'image
                  </button>
                )}
                <input
                  value={hero.title || ""}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder={page.fallback}
                  className="input-dark mt-3 !h-11"
                  data-testid={`settings-hero-${page.key}-title`}
                />
                <textarea
                  rows={2}
                  value={hero.description || ""}
                  onChange={(e) => update({ description: e.target.value })}
                  placeholder="Description du bandeau (facultative)"
                  className="input-dark mt-3 !h-auto py-3"
                  data-testid={`settings-hero-${page.key}-description`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Réseaux sociaux & Google</h3>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Lien Instagram
          </label>
          <input
            value={settings.instagram_url || ""}
            onChange={(e) => setSettings((s) => ({ ...s, instagram_url: e.target.value }))}
            placeholder="https://instagram.com/votrecompte"
            className="input-dark"
            data-testid="settings-instagram-input"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Lien Facebook
          </label>
          <input
            value={settings.facebook_url || ""}
            onChange={(e) => setSettings((s) => ({ ...s, facebook_url: e.target.value }))}
            placeholder="https://facebook.com/votrepage"
            className="input-dark"
            data-testid="settings-facebook-input"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Lien de votre fiche Google (avis)
          </label>
          <input
            value={settings.google_url || ""}
            onChange={(e) => setSettings((s) => ({ ...s, google_url: e.target.value }))}
            placeholder="https://maps.google.com/..."
            className="input-dark"
            data-testid="settings-google-input"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Note Google affichée (ex : 4,8)
          </label>
          <input
            value={settings.google_rating || ""}
            onChange={(e) => setSettings((s) => ({ ...s, google_rating: e.target.value }))}
            placeholder="4,8"
            className="input-dark"
            data-testid="settings-google-rating-input"
          />
          <p className="mt-2 text-xs text-ivory/35">
            Affichée dans la rubrique « Ils parlent de nous ». Laissez vide pour utiliser la note
            calculée des avis du site.
          </p>
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Clé API Google Places (synchro des avis)
          </label>
          <input
            value={settings.google_api_key || ""}
            onChange={(e) => setSettings((s) => ({ ...s, google_api_key: e.target.value }))}
            placeholder="AIza..."
            className="input-dark"
            data-testid="settings-google-api-key"
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
            Place ID Google Business
          </label>
          <input
            value={settings.google_place_id || ""}
            onChange={(e) => setSettings((s) => ({ ...s, google_place_id: e.target.value }))}
            placeholder="ChIJ..."
            className="input-dark"
            data-testid="settings-google-place-id"
          />
          <p className="mt-2 text-xs text-ivory/35">
            Trouvez votre Place ID sur Google Maps (fiche établissement) ou via l'outil « Place ID
            Finder » de Google. La clé API se crée dans Google Cloud Console (API Places activée).
          </p>
        </div>
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Coordonnées & WhatsApp
        </h3>
        {[
          { key: "phone_display", label: "Numéro WhatsApp affiché", placeholder: "07 56 89 53 54" },
          { key: "whatsapp", label: "Numéro WhatsApp international (sans +)", placeholder: "33756895354" },
          { key: "email", label: "Email officiel", placeholder: "lepmaasai@gmail.com" },
          { key: "address_name", label: "Nom (adresse)", placeholder: "Palais Maasaï" },
          { key: "address_street", label: "Rue", placeholder: "87 rue Berbisey" },
          { key: "address_city", label: "Ville", placeholder: "21000 Dijon" },
        ].map((field) => (
          <div key={field.key}>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
              {field.label}
            </label>
            <input
              value={settings.contact?.[field.key] || ""}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  contact: { ...(s.contact || {}), [field.key]: e.target.value },
                }))
              }
              placeholder={field.placeholder}
              className="input-dark"
              data-testid={`settings-contact-${field.key}`}
            />
          </div>
        ))}
      </div>

      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Horaires</h3>
        {[
          { key: "week_label", label: "Jours semaine (ex : Mardi à Vendredi)" },
          { key: "week_1", label: "Horaires semaine — service 1" },
          { key: "week_2", label: "Horaires semaine — service 2" },
          { key: "weekend_label", label: "Jours week-end (ex : Samedi, Dimanche et Lundi)" },
          { key: "weekend_1", label: "Horaires week-end" },
        ].map((field) => (
          <div key={field.key}>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
              {field.label}
            </label>
            <input
              value={settings.hours?.[field.key] || ""}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  hours: { ...(s.hours || {}), [field.key]: e.target.value },
                }))
              }
              className="input-dark"
              data-testid={`settings-hours-${field.key}`}
            />
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Acompte de réservation
        </h3>
        <p className="mt-2 text-xs text-ivory/40">
          Montant par personne, correspondant au minimum au prix d'un plat (menu minimum). Il sera
          multiplié par le nombre de convives et déduit de la facture finale.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="number"
            min="1"
            step="0.5"
            value={settings.deposit_per_person ?? 15}
            onChange={(e) => setSettings((s) => ({ ...s, deposit_per_person: e.target.value }))}
            className="input-dark w-32"
            data-testid="settings-deposit-input"
          />
          <span className="text-sm text-ivory/50">€ par personne</span>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
          Capacité par créneau
        </h3>
        <p className="mt-2 text-xs text-ivory/40">
          Nombre maximum de couverts acceptés par horaire de réservation. Au-delà, le créneau est
          automatiquement refusé (prévention des doubles réservations et surbooking).
        </p>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="number"
            min="1"
            value={settings.capacity_per_slot ?? 40}
            onChange={(e) => setSettings((s) => ({ ...s, capacity_per_slot: e.target.value }))}
            className="input-dark w-32"
            data-testid="settings-capacity-input"
          />
          <span className="text-sm text-ivory/50">couverts par créneau</span>
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">Textes du site</h3>
        {TEXT_FIELDS.map((field) => (
          <div key={field.key}>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ivory/50">
              {field.label}
            </label>
            <textarea
              rows={3}
              value={settings.texts?.[field.key] || ""}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  texts: { ...(s.texts || {}), [field.key]: e.target.value },
                }))
              }
              className="input-dark !h-auto py-3"
              data-testid={`settings-text-${field.key}`}
            />
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} className="btn-primary" data-testid="settings-save-button">
        {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");

  const isAdmin = !user || user.role === "admin";
  const visibleTabs = isAdmin
    ? TABS
    : TABS.filter((t) => t.id !== "equipe" && (user.permissions || []).includes(t.id));
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : visibleTabs[0]?.id || "dashboard";

  useEffect(() => {
    if (user && !isAdmin && visibleTabs.length && !visibleTabs.find((t) => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [user]);

  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="text-sm uppercase tracking-[0.25em] text-ivory/40">Chargement…</p>
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="flex h-16 items-center justify-between border-b border-white/10 px-6">
        <div className="flex items-center gap-3">
          <Monogram className="h-8 w-8" />
          <span className="text-xs font-bold tracking-[0.25em] text-ivory">ADMINISTRATION</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-xs uppercase tracking-[0.15em] text-ivory/50 transition-colors hover:text-gold">
            Voir le site
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-ivory/50 transition-colors hover:text-red-400"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Déconnexion
          </button>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        <nav className="flex gap-1 overflow-x-auto border-b border-white/10 p-4 md:w-56 md:flex-col md:border-b-0 md:border-r md:p-6" data-testid="admin-nav">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-copper/15 text-gold" : "text-ivory/50 hover:text-ivory"
              }`}
            >
              <t.icon className="h-4 w-4" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </nav>

        <main className="flex-1 p-6 md:p-10">
          {activeTab === "dashboard" && <DashboardManager />}
          {activeTab === "reservations" && <ReservationsManager />}
          {activeTab === "clients" && <CrmManager />}
          {activeTab === "commandes" && <OrdersManager />}
          {activeTab === "boutique" && <ProductsManager />}
          {activeTab === "promotions" && <PromotionsManager />}
          {activeTab === "faq" && <FaqManager />}
          {activeTab === "sections" && <SectionsManager />}
          {activeTab === "editeur" && <FreeCanvasEditor />}
          {activeTab === "themes" && <ThemesManager />}
          {activeTab === "styles" && <ElementStylesManager />}
          {activeTab === "agents" && <AgentsManager />}
          {activeTab === "marketing" && <MarketingManager />}
          {activeTab === "notifications" && <NotificationsManager />}
          {activeTab === "medias" && <MediaManager />}
          {activeTab === "sauvegardes" && <BackupsManager />}
          {activeTab === "seo" && <SeoManager />}
          {activeTab === "services" && <ServicesManager />}
          {activeTab === "cartes-cadeaux" && <GiftCardsManager />}
          {activeTab === "carte" && <MenuManager />}
          {activeTab === "evenements" && <EventsManager />}
          {activeTab === "galerie" && <GalleryManager />}
          {activeTab === "avis" && <TestimonialsManager />}
          {activeTab === "navigation" && <NavigationManager />}
          {activeTab === "messages" && <MessagesManager />}
          {activeTab === "design" && <DesignManager />}
          {activeTab === "equipe" && <TeamManager />}
          {activeTab === "securite" && <SecurityManager />}
          {activeTab === "reglages" && <SettingsManager />}
        </main>
      </div>
    </div>
  );
}
